import { Tile, type MapData, tileAt } from "@dogdell/shared";

/** Soft hills from tile type + cheap positional noise. */
export function terrainHeightAt(map: MapData, worldX: number, worldZ: number): number {
  const x0 = Math.floor(worldX);
  const z0 = Math.floor(worldZ);
  // Sidewalk / plaza / pier / courts stay perfectly flat - don't blend in bumpy grass neighbors.
  const here = tileAt(map, x0, z0);
  if (
    here === Tile.Path ||
    here === Tile.Floor ||
    here === Tile.Pier ||
    here === Tile.CourtBasket ||
    here === Tile.CourtTennis ||
    here === Tile.CourtLine
  ) {
    return cellHeight(map, x0, z0);
  }
  const tx = worldX - x0;
  const tz = worldZ - z0;
  const h00 = cellHeight(map, x0, z0);
  const h10 = cellHeight(map, x0 + 1, z0);
  const h01 = cellHeight(map, x0, z0 + 1);
  const h11 = cellHeight(map, x0 + 1, z0 + 1);
  const hx0 = h00 * (1 - tx) + h10 * tx;
  const hx1 = h01 * (1 - tx) + h11 * tx;
  return hx0 * (1 - tz) + hx1 * tz;
}

export function cellHeight(map: MapData, x: number, y: number): number {
  const tile = tileAt(map, x, y);
  if (tile === undefined) {
    return 0;
  }
  // Keep relief very gentle so tile quads stay nearly planar (avoids jagged facets).
  const n = softNoise(x, y);
  switch (tile) {
    case Tile.Water:
      return -0.22 + n * 0.015;
    case Tile.Floor:
      // Plaza / court pavers stay level.
      return 0.055;
    case Tile.CourtBasket:
    case Tile.CourtTennis:
    case Tile.CourtLine:
      // Sports asphalt sits slightly above lawn, level with plaza.
      return 0.05;
    case Tile.Pier:
      // Raised wooden dock over water.
      return 0.42;
    case Tile.Path:
      // Slightly recessed vs lawn so grass reads as a raised curb edge.
      return pathBesideWater(map, x, y) ? 0.42 : 0.012;
    case Tile.Sand:
      return 0.045 + n * 0.02;
    case Tile.Tree:
    case Tile.Rock:
    case Tile.Bush:
    case Tile.Fence:
      return 0.06 + n * 0.04;
    case Tile.Flowers:
      return 0.04 + n * 0.025;
    default:
      // Soft meadow - tiny ripple only, no big hills.
      return 0.03 + n * 0.035;
  }
}

function pathBesideWater(map: MapData, x: number, y: number): boolean {
  return (
    tileAt(map, x - 1, y) === Tile.Water ||
    tileAt(map, x + 1, y) === Tile.Water ||
    tileAt(map, x, y - 1) === Tile.Water ||
    tileAt(map, x, y + 1) === Tile.Water
  );
}

function softNoise(x: number, y: number): number {
  return (
    Math.sin(x * 0.19 + y * 0.14) * 0.35 +
    Math.sin(x * 0.07 - y * 0.17) * 0.28 +
    Math.sin((x + y) * 0.045) * 0.15 +
    Math.sin(x * 0.41 + y * 0.33) * 0.06
  );
}
